import { Box, FormLabel, type SxProps } from "@mui/material";
import Switch from "@mui/material/Switch";
import { useContext, useEffect, useState } from "react";

import ReadOnlyMode from "@/components/Icons/ReadOnlyMode.tsx";
import ConfirmDiscardChanges from "@/components/ModalForms/ConfirmDiscardChanges";
import { useGlobalFlags } from "@/hooks/useGlobalFlags";
import UseStorage from "@/hooks/useStorage";
import { TrackingContext } from "@/models/TrackingStateProvider.tsx";
import { UxContext } from "@/models/UxStateProvider";

export default function ReadOnlyModeToggle({
  type,
  sx,
  setActive,
}: {
  type: "button" | "switch";
  sx?: SxProps;
  setActive?: (active: boolean) => void;
}) {
  const { ampli } = useContext(TrackingContext);
  const { userReadOnlyMode, setUserReadOnlyMode } = useContext(UxContext);
  const [showDoubleConfirm, setShowDoubleConfirm] = useState(false);
  const { readOnlyMode, showDebugMenu } = useGlobalFlags();
  const { getLocalStorage, setLocalStorage } = UseStorage();

  const toggleReadOnlyMode = (currentValue: boolean) => {
    if (currentValue === false && !showDoubleConfirm) {
      ampli.readOnlyMode();
      setUserReadOnlyMode(true);
      setLocalStorage({ key: "userReadOnlyMode", value: true });
      setActive?.(true);
    } else if (currentValue === true && !showDoubleConfirm) {
      setShowDoubleConfirm(() => true);
    }
  };

  useEffect(() => {
    const localStorageUserReadOnlyMode = getLocalStorage("userReadOnlyMode");
    if (localStorageUserReadOnlyMode === true && userReadOnlyMode === false) {
      setUserReadOnlyMode((prev) => (!prev ? true : prev));
    }
  }, [getLocalStorage, userReadOnlyMode, setUserReadOnlyMode]);

  const denyClose = () => {
    setShowDoubleConfirm(() => false);
  };

  const confirmClose = () => {
    setUserReadOnlyMode(() => false);
    setShowDoubleConfirm(() => false);
    setLocalStorage({ key: "userReadOnlyMode", value: false });
    setActive?.(false);
  };

  useEffect(() => {
    setActive?.(userReadOnlyMode);
  }, [setActive, userReadOnlyMode]);

  if (!readOnlyMode && !showDebugMenu) {
    return;
  }
  return (
    <>
      {showDoubleConfirm && (
        <ConfirmDiscardChanges
          id="disable-read-only-mode"
          visible={showDoubleConfirm}
          deleteText="Confirm"
          label="Disable read only mode?"
          text="Any active channel you are in may be marked as read."
          denyCloseHandler={denyClose}
          confirmCloseHandler={confirmClose}
        />
      )}

      {type === "button" && (
        <Box
          sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => toggleReadOnlyMode(userReadOnlyMode)}
        >
          <ReadOnlyMode />
        </Box>
      )}

      {type === "switch" && (
        <FormLabel
          sx={{
            cursor: "pointer",
            width: "auto",
            display: "inline-flex",
            alignItems: "center",
            ...sx,
          }}
        >
          <Switch size="small" checked={userReadOnlyMode} onChange={() => toggleReadOnlyMode(userReadOnlyMode)} />
          <Box sx={{ ml: 2 }}>Read Only Mode</Box>
        </FormLabel>
      )}
    </>
  );
}
